import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";

import {
  useErrors,
  usePasswordValidation,
  useSignupIdentifiers,
} from "@auth0/auth0-acul-react/signup";
import type {
  ErrorItem,
  IdentifierType,
  PasswordValidationResult,
  SignupPayloadOptions,
} from "@auth0/auth0-acul-react/types";

import Captcha from "@/components/Captcha/index";
import { ULThemeFloatingLabelField } from "@/components/form/ULThemeFloatingLabelField";
import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import PasswordConfirmationField from "@/components/PasswordConfirmationField";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { ULThemeButton } from "@/components/ULThemeButton";
import ULThemeCountryCodePicker from "@/components/ULThemeCountryCodePicker";
import { ULThemeAlert, ULThemeAlertTitle } from "@/components/ULThemeError";
import { ULThemePasswordField } from "@/components/ULThemePasswordField";
import { ULThemePasswordValidator } from "@/components/ULThemePasswordValidator";
import { useCaptcha } from "@/hooks/useCaptcha";
import { transformAuth0CountryCode } from "@/utils/helpers/countryUtils";
import {
  getIndividualIdentifierDetails,
  normalizeSignupIdentifiers,
} from "@/utils/helpers/identifierUtils";

import { useSignupManager } from "../hooks/useSignupManager";

function SignupForm() {
  const {
    handleSignup,
    handlePickCountryCode,
    isCaptchaAvailable,
    transaction,
    texts,
    captcha,
    locales,
  } = useSignupManager();

  const { errors, hasError, dismiss } = useErrors();

  const form = useForm<SignupPayloadOptions>({
    defaultValues: {
      email: "",
      phoneNumber: "",
      username: "",
      password: "",
      captcha: "",
    },
    reValidateMode: "onBlur",
  });

  const {
    formState: { isSubmitting },
    watch,
  } = form;

  // Password validation
  const passwordValue = watch("password");
  const {
    isValid: isPasswordValid,
    results: passwordResults,
  }: PasswordValidationResult = usePasswordValidation(passwordValue || "");

  // Get identifiers from transaction
  const enabledIdentifiers = useSignupIdentifiers();

  // Normalizing drops username (not offered by this template) and collapses
  // duplicates so a misconfigured connection cannot render the same identifier
  // field twice.
  const { required: requiredIdentifiers, optional: optionalIdentifiers } =
    useMemo(
      () => normalizeSignupIdentifiers(enabledIdentifiers),
      [enabledIdentifiers]
    );

  // Handle country code selection for phone input
  const handleCountryCodeSelect = useCallback(() => {
    handlePickCountryCode();
  }, [handlePickCountryCode]);

  // Handle form submission
  const onSubmit = async (values: SignupPayloadOptions) => {
    await handleSignup(values);
  };

  // Get field-specific errors using SDK's errors helper
  const getIdentifierError = useCallback(
    (identifierType: IdentifierType) => {
      const fieldErrors = errors.byField(identifierType);
      return fieldErrors.length > 0 ? fieldErrors[0].message : undefined;
    },
    [errors]
  );

  const captchaSDKError = errors.byField("captcha")[0]?.message;
  const passwordSDKError = errors.byField("password")[0]?.message;

  // Get general errors (errors without a specific field)
  const generalErrors: ErrorItem[] = errors
    .byType("auth0")
    .filter((err) => !err.field);

  // Use locale strings with fallback to SDK texts
  const passwordLabel = texts?.passwordPlaceholder
    ? `${texts.passwordPlaceholder}*`
    : `${locales.form.fields.password.label}*`;
  const captchaLabel = texts?.captchaCodePlaceholder
    ? `${texts.captchaCodePlaceholder}*`
    : `${locales.form.fields.captcha.label}*`;
  const buttonText = texts?.buttonText || locales.form.button;
  const passwordSecurityText =
    texts?.passwordSecurityText || locales.form.passwordSecurity;

  // Setup captcha with useCaptcha hook
  const { captchaConfig, captchaProps } = useCaptcha(
    captcha || undefined,
    captchaLabel
  );

  // Transform the phone country code for display
  const phoneCountryCode = transformAuth0CountryCode(
    transaction?.countryCode,
    transaction?.countryPrefix
  );

  // Render identifier fields helper function
  const renderIdentifierField = useCallback(
    (identifierType: IdentifierType, isRequired: boolean) => {
      if (identifierType === "phone") {
        return (
          <div
            key={`${isRequired ? "required" : "optional"}-phone-container`}
            className="space-y-2"
          >
            <ULThemeCountryCodePicker
              selectedCountry={phoneCountryCode}
              onClick={handleCountryCodeSelect}
              fullWidth
              placeholder="Select Country"
            />
            <FormField
              control={form.control}
              name="phoneNumber"
              rules={{
                required: isRequired
                  ? locales.form.fields.common.required
                  : false,
              }}
              render={({ field, fieldState }) => {
                const { label, type, autoComplete } =
                  getIndividualIdentifierDetails("phone", isRequired, texts);
                const sdkError = getIdentifierError("phone");

                return (
                  <FormItem>
                    <ULThemeFloatingLabelField
                      {...field}
                      value={String(field.value || "")}
                      label={label}
                      type={type}
                      autoComplete={autoComplete}
                      error={!!fieldState.error || !!sdkError}
                    />
                    <ULThemeFormMessage
                      sdkError={sdkError}
                      hasFormError={!!fieldState.error}
                    />
                  </FormItem>
                );
              }}
            />
          </div>
        );
      }

      // Handle other identifier types (e.g. email)
      return (
        <FormField
          key={identifierType}
          control={form.control}
          name={identifierType}
          rules={{
            required: isRequired ? locales.form.fields.common.required : false,
          }}
          render={({ field, fieldState }) => {
            const { label, type, autoComplete } =
              getIndividualIdentifierDetails(identifierType, isRequired, texts);
            const sdkError = getIdentifierError(identifierType);

            return (
              <FormItem>
                <ULThemeFloatingLabelField
                  {...field}
                  label={label}
                  type={type}
                  autoComplete={autoComplete}
                  error={!!fieldState.error || !!sdkError}
                />
                <ULThemeFormMessage
                  sdkError={sdkError}
                  hasFormError={!!fieldState.error}
                />
              </FormItem>
            );
          }}
        />
      );
    },
    [
      form.control,
      locales.form.fields.common.required,
      phoneCountryCode,
      handleCountryCodeSelect,
      texts,
      getIdentifierError,
    ]
  );

  // Render fields helper function
  const renderFields = useCallback(
    (identifiers: IdentifierType[], isRequired: boolean) =>
      identifiers.map((identifierType) =>
        renderIdentifierField(identifierType, isRequired)
      ),
    [renderIdentifierField]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {/* Display general errors */}
        {hasError && generalErrors.length > 0 && (
          <div className="space-y-3 mb-4">
            {generalErrors.map((error) => (
              <ULThemeAlert
                key={error.id}
                variant="destructive"
                onDismiss={() => dismiss(error.id)}
              >
                <ULThemeAlertTitle>
                  {error.message || locales.errors.general}
                </ULThemeAlertTitle>
              </ULThemeAlert>
            ))}
          </div>
        )}

        {/* Required identifier fields first */}
        {renderFields(requiredIdentifiers, true)}

        {/* Optional identifier fields */}
        {renderFields(optionalIdentifiers, false)}

        {/* Password Field */}
        <FormField
          control={form.control}
          name="password"
          rules={{
            required: locales.form.fields.password.required,
            validate: () => {
              if (!isPasswordValid)
                return locales.form.fields.password.doesNotMeetRequirements;
              return true;
            },
          }}
          render={({ field, fieldState }) => (
            <FormItem>
              <ULThemePasswordField
                {...field}
                value={String(field.value || "")}
                label={passwordLabel}
                autoComplete="new-password"
                error={!!fieldState.error || !!passwordSDKError}
              />
              <ULThemeFormMessage
                sdkError={passwordSDKError}
                hasFormError={!!fieldState.error}
              />
            </FormItem>
          )}
        />

        {/* Confirm Password Field — local-only comparison, never submitted */}
        <PasswordConfirmationField password={passwordValue || ""} />

        {/* Captcha Field */}
        {isCaptchaAvailable && captchaConfig && (
          <Captcha
            control={form.control}
            name="captcha"
            captcha={captchaConfig}
            {...captchaProps}
            sdkError={captchaSDKError}
            rules={{
              required: locales.form.fields.captcha.required,
            }}
          />
        )}

        {/* Password Validation Rules */}
        <ULThemePasswordValidator
          validationRules={passwordResults}
          passwordSecurityText={passwordSecurityText}
          show={!!passwordValue}
        />

        {/* Submit Button */}
        <ULThemeButton type="submit" className="w-full" disabled={isSubmitting}>
          {buttonText}
        </ULThemeButton>
      </form>
    </Form>
  );
}

export default SignupForm;
